<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      v-if="!inPopup"
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('order.orders') }}
        </span>
        <h3 class="page-title">
          <template v-if="isDraftsView">
            {{ $t('order.draftsList') }}
          </template>
          <template v-else>
            {{ $t('order.ordersList') }}
          </template>
        </h3>
      </d-col>

      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          class="ml-2"
          @click.prevent="$router.push('/zamowienia/nowe')">
          <i class="material-icons">add</i>
          {{ $t('ui.add') }}
        </d-button>
      </d-col>
    </d-row>

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="orders-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex orders-filter">
        <div class="d-flex mb-2">
          <d-input
            v-model="filters.order_name"
            :placeholder="$t('ui.orderName')"
            class="w-50" />

          <date-range-picker
            ref="picker"
            :locale-data="{
              'firstDay': 1,
              'format': 'DD-MM-YYYY',
              'daysOfWeek': [$t('ui.daysOfWeek.sun'), $t('ui.daysOfWeek.mon'), $t('ui.daysOfWeek.tue'), $t('ui.daysOfWeek.wed'), $t('ui.daysOfWeek.thur'), $t('ui.daysOfWeek.fri'), $t('ui.daysOfWeek.sat')],
              'monthNames': [$t('ui.monthNames.jan'), $t('ui.monthNames.feb'), $t('ui.monthNames.mar'), $t('ui.monthNames.apr'), $t('ui.monthNames.may'), $t('ui.monthNames.jun'), $t('ui.monthNames.jul'), $t('ui.monthNames.aug'), $t('ui.monthNames.sep'), $t('ui.monthNames.oct'), $t('ui.monthNames.nov'), $t('ui.monthNames.dec')],
            }"
            class="ml-2 w-50"
            style="min-width: 200px;"
            :singleDatePicker="false"
            :timePicker="false"
            :showWeekNumbers="false"
            :showDropdowns="false"
            :autoApply="true"
            v-model="dateRange"
            :ranges="false"
            :linkedCalendars="true">
            <template v-slot:input="picker" style="min-width: 350px;">
              {{ formatDateStart(picker.startDate) }} - {{ formatDateEnd(picker.endDate) }}
            </template>
          </date-range-picker>

          <v-select
            v-if="!isDraftsView"
            class="ml-2 w-50"
            :options="orderStatusOptions"
            v-model="filters.status"
            :placeholder="$t('status.statusQuestionMark')"
            :searchable="false"
            label="text">
          </v-select>
        </div>

        <div class="d-flex mb-2">
          <select-customer
            v-if="!(inPopup && forcedCustomerID)"
            :customerID="filters.partner_customer_id"
            :showMe="true"
            class="w-50" />

          <v-select
            v-if="!(inPopup && forcedCustomerID)"
            class="ml-2 w-50"
            :options="countriesOptions"
            v-model="filters.country_id"
            :placeholder="$t('customer.customerCountryQuestionMark')"
            :searchable="false"
            label="text">
          </v-select>
        </div>

        <div class="d-flex">
          <v-select
            class="mr-2 w-50"
            :options="productCodesOptions"
            v-model="filters.productCode"
            ref="product-codes-filter"
            :placeholder="$t('product.productsCodes')"
            :searchable="true"
            :multiple="true"
            label="text"
            :filter="filteredProductCodes">
            <template slot="no-options">
              <template v-if="$refs['product-codes-filter'] && $refs['product-codes-filter'].search.length">
                {{ $t('product.noProductsMatchingGivenPhrase') }}&hellip;
              </template>
              <template v-else>
                {{ $t('product.noProductsMatchingGivenPhrase') }}
              </template>
            </template>
          </v-select>

          <v-select
            class="ml-2 mr-2 w-50"
            :options="productTagsOptions"
            v-model="filters.productTags"
            :placeholder="$t('order.productsTags')"
            :searchable="true"
            :multiple="true"
            label="text">
            <template slot="no-options">
              {{ $t('order.noTagsMatchingGivenPhrase') }}&hellip;
            </template>
          </v-select>

          <d-button
            theme="info"
            outline
            class="text-nowrap ml-auto"
            @click.prevent="validateFiltersForm">{{ $t('ui.search') }}</d-button>

          <d-button
            theme="danger"
            outline
            class="text-nowrap ml-2"
            @click.prevent="clearFilters">{{ $t('ui.resetFilters') }}</d-button>
        </div>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('order.customerAndName') }}</th>
            <th scope="col" class="border-0" style="width: 180px;">{{ $t('status.status') }}</th>
            <th scope="col" class="border-0" style="width: 240px;">{{ $t('ui.value') }}</th>
            <th scope="col" class="border-0" style="width: 240px;">{{ $t('order.offerSubmissionDate') }}</th>
            <th scope="col" class="border-0" style="width: 250px;">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loadError">
          <tr
            v-for="(order, index) of orders"
            :key="'order-' + index"
            :class="{
              'has-been-last-visited': lastViewedItem.id === order.id
            }">
            <td class="align-middle customer-info">
              <d-badge
                v-if="order.blockade_status === 'BLOCKADE'"
                theme="danger"
                class="mb-2 mr-2">
                {{ $t('order.blockade') }}
              </d-badge>
              <br class="br-to-hide">
              <template v-if="order.partner_customer_id">
                {{ order.partner_customer_name }}<br>
              </template>
              <template v-else>
                {{ order.customer_name }}<br>
              </template>
              <small v-if="order.realization_date">
                <strong>{{ $t('ui.deadlinePreferred') }}:</strong> {{ order.realization_date }}
              </small>
              <br v-if="order.realization_date">
              <small>
                <strong>{{ $t('ui.refNumber') }}: </strong>
                <span class="text-primary">#{{ order.id }}</span>
              </small>
              <br v-if="order.order_name">
              <small>
                <strong>{{ $t('ui.orderName') }}: </strong>
                <span class="text-primary">{{ order.order_name }}</span>
              </small>
            </td>

            <template v-if="inPopup">
              <td class="align-middle">
                {{ order.created_at }}<br>
                <small>({{ $t('order.lastChange') }} {{ order.updated_at }})</small>
              </td>

              <td class="align-middle">
                <d-badge
                  :theme="getStatusClass(order.status)"
                  :outline="getOutline(order.status, order.collect_user_id)">
                  {{ getStatusName(order.status, order.collect_user_id) }}
                </d-badge><br>

                <small>
                  {{ $t('ui.productsGenitive') }}: <strong>{{ order.products.length }}</strong>
                </small>
                <br>
                <small>
                  {{ $t('order.servicesGenitive') }}: <strong>{{ getServicesCount(order.products, order.services) }}</strong>
                </small>
              </td>

              <td class="align-middle">
                {{ Number(order.price).toFixed(2) }} {{ getCurrency(order.currency_id) }}<br>
                <small>
                  {{ $t('order.invoicesIssued') }}: <strong>{{ order.settlements.length }}</strong>
                </small>
              </td>
            </template>

            <template v-else>
              <td class="align-middle">
                <d-badge
                  v-if="lastViewedItem.id === order.id"
                  theme="warning"
                  class="w-100 text-center mb-1">
                  <template v-if="lastViewedItem.action === 'viewed'">
                    {{ $t('order.lastItemViewed') }}
                  </template>
                  <template v-else>
                    {{ $t('order.lastItemModified') }}
                  </template>
                </d-badge>

                <d-badge
                  :theme="getStatusClass(order.status)"
                  :outline="getOutline(order.status, order.collect_user_id)"
                  class="w-100">
                  {{ getStatusName(order.status, order.collect_user_id) }}
                </d-badge><br>

                <small>
                  {{ $t('ui.productsGenitive') }}: <strong>{{ order.products.length }}</strong>
                </small>
                <br>
                <small>
                  {{ $t('order.servicesGenitive') }}: <strong>{{ getServicesCount(order.products, order.services) }}</strong>
                </small>
              </td>
              <td class="align-middle">
                {{ Number(order.price).toFixed(2) }} {{ getCurrency(order.currency_id) }}<br>
                <small v-if="order.partner_customer_id">
                  {{ $t('order.valueForPartnerCustomer') }}:
                  <strong>{{ Number(order.price_for_partner).toFixed(2) }} {{ getCurrency(order.currency_id) }}</strong><br>
                </small>
                <small>
                  {{ $t('order.invoicesIssued') }}: <strong>{{ order.settlements.length }}</strong>
                </small>
              </td>
              <td class="align-middle">
                {{ order.created_at }}<br>
                <small>({{ $t('order.lastChange') }} {{ order.updated_at }})</small>
              </td>
            </template>

            <td class="align-middle">
              <d-button
                v-if="!(inPopup && forcedCustomerID) && order.status !== 'DRAFT'"
                theme="primary"
                @click.native="orderActionUrl(order.id, order.status)"
                class="mr-2"
                :title="$t('order.see')">
                <i class="material-icons">visibility</i>
              </d-button>

              <d-button
                v-if="!inPopup && order.status === 'DRAFT'"
                theme="primary"
                @click.native="orderActionUrl(order.id, order.status)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>

              <d-button
                theme="primary"
                :outline="true"
                @click.native="duplicateOrder(order.id)"
                :title="$t('ui.duplicate')"
                class="mr-2"
                :disabled="duplicateInProgress">
                <i class="material-icons">content_copy</i>
              </d-button>

              <d-button
                v-if="!inPopup && order.status === 'DRAFT'"
                theme="danger"
                @click.native="deleteDraft(order.id)"
                :title="$t('ui.delete')"
                :disabled="removeInProgress">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="orders.length === 0">
            <td
              colspan="8"
              class="text-center">
              {{ $t('order.noOffersOrOrdersToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import CommonGetters from './../utils/CommonGetters.js';
import CustomerPanel from './../mixins/CustomerPanel.vue';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import orderStatusClasses from './../data/order-status-classes.js';
import orderStatuses from './../data/order-statuses.js';
import Pagination from './../components/common/Pagination.vue';
import SelectCustomer from './../components/common/SelectCustomer.vue';
import { mapGetters } from 'vuex';
import Vue from 'vue';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';

export default {
  name: 'orders',
  mixins: [
    CustomerPanel,
    HasFilters,
    HasPagination
  ],
  props: {
    inPopup: {
      default: false
    },
    popupFilter: {
      default: ''
    },
    showWorkers: {
      default: false
    },
    dashboardParam: {
      default: false
    },
    popupShowAll: {
      default: false
    },
    forcedCustomerID: {
      default: false
    },
    customerPopupContent: {
      default: false
    }
  },
  components: {
    'date-range-picker': DateRangePicker,
    'pagination': Pagination,
    'select-customer': SelectCustomer
  },
  watch: {
    'filters.show_all': {
      handler () {
        if (this.allDataLoaded) {
          this.debouncedLoadFilteredData();
        }
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters([
      'getUserID'
    ]),
    addNewUrl () {
      return '/zamowienia/nowe/';
    },
    allDataLoaded () {
      return this.loaded && this.loadedCurrencies && this.loadedProducts;
    },
    countriesOptions () {
      return this.countries.map(country => ({
        text: country.name,
        value: country.id
      }));
    },
    isDraftsView () {
      return this.$route.path.indexOf('/oferty') > -1;
    },
    orderStatusOptions () {
      if (this.isDraftsView) {
        return false;
      }

      return [
        {
          text: this.$t('order.toCollect'),
          value: 'TO_COLLECT'
        },
        {
          text: this.$t('order.waitingPurchaseProducts'),
          value: 'WAITING_PURCHASE_PRODUCTS'
        },
        {
          text: this.$t('ui.order'),
          value: 'ORDER'
        },
        {
          text: this.$t('order.inProduction'),
          value: 'IN_PRODUCTION'
        },
        {
          text: this.$t('order.toCheckInWarehouse'),
          value: 'TO_CHECK'
        },
        {
          text: this.$t('order.readyForShipping'),
          value: 'PREPARATION_FOR_SHIPPING'
        },
        {
          text: this.$t('order.waitingForShipping'),
          value: 'WAITING_FOR_SHIPPING'
        },
        {
          text: this.$t('order.sent'),
          value: 'SENT'
        },
        {
          text: this.$t('order.pendingPayment'),
          value: 'PENDING_PAYMENT'
        },
        {
          text: this.$t('order.ended'),
          value: 'ENDED'
        },
        {
          text: this.$t('order.toDelete'),
          value: 'TO_DELETE'
        },
        {
          text: this.$t('order.deleted'),
          value: 'DELETED'
        }
      ];
    },
    productTagsOptions () {
      let options = [];

      for (let i = 0; i < this.productTags.length; i++) {
        for (let j = 0; j < this.productTags[i].tags.length; j++) {
          let tagName = this.productTags[i].tags[j].name;
          let tagID = this.productTags[i].tags[j].id;
          let tagCategoryID = this.productTags[i].tags[j].category_id;
          let tagCategoryName = this.productTags[i].name;

          let option = {
            text: tagCategoryName + ': ' + tagName,
            value: tagCategoryID + ';' + tagID
          };

          options.push(option);
        }
      }

      return options;
    },
    productCodesOptions () {
      let options = [];

      if (this.$refs['product-codes-filter'] && this.$refs['product-codes-filter'].search.length < 1) {
        return options;
      }

      for (let i = 0; i < this.products.length; i++) {
        let option = {
          text: this.products[i].code,
          value: '[#' + this.products[i].id + '#]' + this.products[i].code
        };

        options.push(option);
      }

      return options;
    },
    paidStatusOptions () {
      return [
        {
          text: this.$t('ui.settled'),
          value: 'PAID'
        },
        {
          text: this.$t('ui.notSettled'),
          value: 'UNPAID'
        }
      ];
    }
  },
  data() {
    return {
      currencies: [],
      orders: [],
      workers: [],
      countries: [],
      productTags: [],
      orderTags: [],
      products: [],
      loaded: false,
      loadedCountries: false,
      loadedCurrencies: false,
      loadedWorkers: false,
      loadedProductTags: false,
      loadedProducts: false,
      loadedOrderTags: false,
      loadError: false,
      duplicateInProgress: false,
      removeInProgress: false,
      skipLSPagination: false,
      dateRange: {
        startDate: null,
        endDate: null
      },
      filters: {
        order_name: '',
        tracking_number: '',
        settlement_number: '',
        partner_customer_id: null,
        status: null,
        settlement: null,
        user_id: null,
        country_id: null,
        productTags: null,
        productCode: null,
        orderTags: null,
        show_all: false
      },
      // helpers
      lastViewedItem: {
        action: '',
        id: null
      },
      downloadInProgress: false
    };
  },
  created () {
    if (this.inPopup) {
      this.skipLSPagination = true;
    }
  },
  mounted () {
    if (!this.$root.orderUnblockInProgress) {
      this.loadFilteredData();
    }

    this.loadAdditionalData();
    this.$bus.$on('ui-select-customer-updated', this.setCustomerID);
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let whereData = {};
      let allItemsParam = '';

      if (this.filters.order_name && this.filters.order_name !== '') {
        whereData.order_name = this.filters.order_name;
      }

      if (this.filters.tracking_number && this.filters.tracking_number !== '') {
        whereData.tracking_number = this.filters.tracking_number;
      }

      if (this.filters.settlement_number && this.filters.settlement_number !== '') {
        whereData.settlement_number = this.filters.settlement_number;
      }

      if (this.filters.partner_customer_id || this.filters.partner_customer_id === 0) {
        whereData.partner_customer_id = this.filters.partner_customer_id;
      }

      if (this.inPopup && this.forcedCustomerID) {
        whereData.partner_customer_id = this.forcedCustomerID;
      }

      if (this.filters.country_id) {
        whereData.country_id = this.filters.country_id.value;
      }

      if (this.filters.productTags) {
        if (!whereData['products']) {
          whereData.products = {
            tags: []
          };
        }

        whereData['products']['tags'] = this.prepareProductTagsFilter(this.filters.productTags);
      }

      if (this.filters.orderTags) {
        whereData['tags'] = this.prepareOrderTagsFilter(this.filters.orderTags);
      }

      if (this.filters.productCode) {
        if (!whereData['products']) {
          whereData.products = {
            code: []
          };
        }

        whereData['products']['code'] = this.prepareProductCodesFilter(this.filters.productCode);
      }

      if (this.filters.status !== null) {
        whereData.status = this.filters.status.value;
      }

      if (this.filters.invoice_request !== null && typeof this.filters.invoice_request !== 'undefined') {
        whereData.invoice_request = this.filters.invoice_request.value;
      }

      if (this.filters.settlement !== null) {
        whereData['settlement-status'] = this.filters.settlement.value;
      }

      if (this.dateRange.startDate) {
        whereData['created_at_from'] = this.prepareDateString(this.dateRange.startDate);
      }

      if (this.dateRange.endDate) {
        whereData['created_at_to'] = this.prepareDateString(this.dateRange.endDate);
      }

      if (this.filters.user_id) {
        whereData.user_id = this.filters.user_id.value;
      }

      if (this.isDraftsView) {
        whereData.status = ['DRAFT'];
      } else {
        whereData.excluded_status = ['DRAFT'];
      }

      let requestConfig = {
        with: ['settlements', 'orderProducts', 'purchase', 'emails', 'complaints'],
        where: whereData
      };

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: this.getApiEndpoint('/orders/items?page=' + this.currentPage + allItemsParam.replace('?', '&')),
        listField: 'orders',
        loadError: 'loadError',
        params: requestConfig,
        successAction () {
          Vue.nextTick(() => {
            if (document.querySelector('tr.has-been-last-visited')) {
              document.querySelector('tr.has-been-last-visited').scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center'
              });
            }
          });
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        order_name: '',
        tracking_number: '',
        settlement_number: '',
        partner_customer_id: null,
        status: null,
        settlement: null,
        user_id: null,
        country_id: null,
        exclamation: null,
        productTags: null,
        orderTags: null,
        productCode: null
      };
      this.dateRange = {
        startDate: null,
        endDate: null
      };

      this.$bus.$emit('ui-select-customer-reset');
      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: this.getApiEndpoint('/currencies/items'),
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: this.getApiEndpoint('/products/all-items-id-code'),
        loadedKey: 'loadedProducts',
        outputKey: 'products',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: this.getApiEndpoint('/tags/categories/items'),
        loadedKey: 'loadedProductTags',
        params: {
          with: ['tags']
        },
        outputKey: 'productTags',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: this.getApiEndpoint('/countries/items'),
        loadedKey: 'loadedCountries',
        outputKey: 'countries',
        errorKey: 'loadError'
      });
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['orders-filters-form'], this.debouncedLoadFilteredData);
    },
    getCurrency (currencyID) {
      return CommonGetters.getCurrencyById(currencyID, this.currencies);
    },
    getStatusName (status, orderIsCollectedBy) {
      let statusName = ListUtils.getKeyLabel(orderStatuses[this.$i18n.locale], status);

      if (status === 'TO_COLLECT' && orderIsCollectedBy === this.getUserID) {
        return this.$t('order.beeingCollected');
      }

      if (status === 'SENT_TO_CUSTOMER' && this.userIsCustomer) {
        return this.$t('order.forAcceptance');
      }

      return statusName;
    },
    getStatusClass (status) {
      return ListUtils.getKeyLabel(orderStatusClasses, status).replace(' outline', '');
    },
    getOutline (status, orderIsCollectedBy) {
      if (status === 'TO_COLLECT' && orderIsCollectedBy === this.getUserID) {
        return false;
      }

      return ListUtils.getKeyLabel(orderStatusClasses, status).indexOf(' outline') > -1;
    },
    setCustomerID (customerData) {
      this.filters.partner_customer_id = customerData.customer_id;
    },
    getServicesCount (products, services) {
      let productsCount = products.map(product => product.services.length).reduce((a, b) => a + b, 0);
      let servicesCount = 0;

      if (services && services.length > 0) {
        servicesCount = services.length;
      }

      return productsCount + servicesCount;
    },
    prepareDateString (input) {
      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    prepareProductTagsFilter (productTags) {
      let output = {};

      for (let i = 0; i < productTags.length; i++) {
        let tagData = productTags[i].value.split(';');
        let tagCategoryID = parseInt(tagData[0], 10);
        let tagID = parseInt(tagData[1], 10);

        if (output[tagCategoryID]) {
          output[tagCategoryID].push(tagID);
        } else {
          output[tagCategoryID] = [tagID];
        }
      }

      return output;
    },
    prepareProductCodesFilter (productCode) {
      let output = [];

      for (let i = 0; i < productCode.length; i++) {
        output.push(productCode[i].value.replace(/\[\#[0-9]{1,}\#\]/gmi, ''));
      }

      return output;
    },
    filterByProductCodes (option, label, search) {
      return (option.text).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    filteredProductCodes (options, search) {
      return options.filter(option => this.filterByProductCodes(option, option.id, search)).slice(0, 50);
    },
    formatDateStart (input) {
      if (input === null) {
        return this.$t('ui.searchFrom');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    formatDateEnd (input) {
      if (input === null) {
        return this.$t('ui.searchTo');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    retrieveLastViewedData () {
      if (!localStorage.getItem('headwear-crm-warehouse-last-viewed')) {
        return;
      }

      let data = localStorage.getItem('headwear-crm-warehouse-last-viewed').split(';');

      if (data[0] && data[1] && data[2] && parseInt(data[2], 10) > +new Date() - (1 * 60 * 1000)) {
        this.lastViewedItem = {
          action: data[0],
          id: parseInt(data[1], 10)
        };
      }

      localStorage.removeItem('headwear-crm-warehouse-last-viewed');
    },
    orderActionUrl (orderID, status) {
      if (this.isDraftsView || status === 'DRAFT') {
        return this.$router.push('/oferty/edycja/' + orderID);
      }

      return this.$router.push('/zamowienia/podglad/' + orderID);
    },
    deleteDraft (orderID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: this.getApiEndpoint('/orders/delete/'),
        id: orderID,
        successTitle: this.$t('order.draftDeleteSuccessTitle'),
        successText: this.$t('order.draftDeleteSuccessText'),
        confirmTitle: this.$t('order.draftDeleteConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.draftDeleteErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    duplicateOrder (orderID) {
      Vue.swal({
        title: this.$t('order.duplicateOrder.questionTitle'),
        text: this.isDraftsView ? this.$t('order.duplicateOrder.questionTextOffer') : this.$t('order.duplicateOrder.questionTextOrder'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('ui.yes'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          this.duplicateInProgress = true;

          this.$http.post('/customer-panel/orders/duplicate/' + orderID).then(response => {
            if (response.data.success) {
              Vue.swal({
                title: this.isDraftsView ? this.$t('order.duplicateOrder.successTitleOffer') : this.$t('order.duplicateOrder.successTitleOrder'),
                html: this.isDraftsView ? this.$t('order.duplicateOrder.successTextOffer') : this.$t('order.duplicateOrder.successTextOrder'),
                type: 'success',
                confirmButtonText: this.$t('ui.ok'),
                buttonsStyling: true
              });
            } else {
              Vue.swal({
                title: this.$t('ui.errorOccured'),
                html: this.$t('ui.tryAgain'),
                type: 'warning',
                confirmButtonText: this.$t('ui.ok'),
                buttonsStyling: true
              });
            }

            this.duplicateInProgress = false;
            this.loadFilteredData();
          }).catch(error => {
            console.log(error);
            Vue.swal({
              title: this.$t('ui.errorOccured'),
              html: this.$t('ui.tryAgain'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });

            this.duplicateInProgress = false;
          });
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('ui-select-customer-updated', this.setCustomerID);
  }
};
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}

.orders-filter {
  & > div {
    width: 100%;
  }

  flex-wrap: wrap;

  .form-control.is-select {
    border: none;
  }
}

tr.has-been-last-visited {
  td {
    border-top: 8px solid #ffb400;
  }
}

body .orders-filter /deep/ .vs__dropdown-toggle {
  max-height: none!important;
}

.br-to-hide:first-child {
  display: none;
}
</style>
